import { accessExecutionGrantV1Schema } from "./access-control.js";
import { resourceAccessSchema } from "./resource-references.js";
import { z } from "zod";
import { DEFAULT_DASHBOARD_DATE_PRESETS, dashboardDatePresetIds, dashboardDatePresetSchema, } from "./dashboard-date-presets.js";
import { CHANNEL_OBJECT_FIELD_TYPE_SELECTION_GUIDANCE, channelObjectFieldTypeSchema, } from "./channel-field-types.js";
export { CHANNEL_OBJECT_FIELD_TYPES, CHANNEL_OBJECT_FIELD_TYPE_CATALOG, CHANNEL_OBJECT_FIELD_TYPE_SELECTION_GUIDANCE, channelFieldTypesShareValues, channelObjectFieldTypeDefinitionSchema, channelObjectFieldTypeSchema, suggestChannelObjectFieldType, } from "./channel-field-types.js";
import { sourceBackedChannelInputSchema, twinProductChannelInputSchema } from "./source-backed-channel.js";
import { createSourceActionReceiptRequestSchema } from "./inbox.js";
import { twinDoctorImpactQuerySchema, twinDoctorImpactResponseSchema } from "./twin-doctor.js";
import { addEmailAttachmentsCommandInputSchema, emailAttachmentAddResultSchema, emailAttachmentSearchResultSchema, findEmailAttachmentsCommandInputSchema, } from "./email-attachments.js";
import { curatedViewProposalResultSchema, proposeCuratedViewCommandInputSchema, } from "./curated-view-proposals.js";
import { channelFeedCommentSchema, channelFeedEngagementSchema, channelFeedThreadSchema, channelPostSchema, createChannelFeedCommentCommandInputSchema, createChannelPostCommandInputSchema, deleteChannelFeedCommentCommandInputSchema, deleteChannelPostCommandInputSchema, getChannelFeedThreadCommandInputSchema, listChannelFeedResponseSchema, setChannelFeedReactionCommandInputSchema, updateChannelFeedCommentCommandInputSchema, updateChannelPostCommandInputSchema, } from "./channel-feed.js";
import { DASHBOARD_DEFAULT_LAYOUT, dashboardLayoutIssues, dashboardLayoutSchema, dashboardWidgetPositionSchema, withLeastRows, } from "./dashboard-layout.js";
import { channelDataFreshnessSchema, channelDataSourceSchema, } from "./channel-data-trace.js";
export const channelIdSchema = z.string().trim().min(1);
export const channelObjectTypeIdSchema = z.string().trim().min(1);
export const channelObjectFieldIdSchema = z.string().trim().min(1);
export const channelRecordIdSchema = z.string().trim().min(1);
export const channelViewIdSchema = z.string().trim().min(1);
export const channelFileIdSchema = z.string().trim().min(1);
export const channelSlugSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/u);
export const channelVisibilitySchema = z.enum(["private", "shared"]);
export const channelStatusSchema = z.enum(["active", "archived"]);
/** Internal channels are for the organisation's members; external ones can also admit invited guests. */
export const channelAudienceSchema = z.enum(["internal", "external"]);
export const channelViewStatusSchema = z.enum(["active", "archived", "deleted"]);
export const channelMemberRoleSchema = z.enum(["owner", "member"]);
export const channelMemberStatusSchema = z.enum(["active", "removed"]);
export const channelPrincipalTypeSchema = z.enum(["user", "service"]);
// Data set accents are identity, not meaning: a fixed palette keeps every tone
// legible in both themes and keeps stored values portable across surfaces. The
// picker shows the palette as two rows of seven. Derived defaults (a data set or
// field option with no stored accent) hash into the first row only, so nothing
// changes colour when the palette grows and every client that derives the same
// way stays in step; the second row is reached by choice.
export const channelDataSetAccentRows = [
    ["sky", "violet", "teal", "amber", "green", "orange", "red"],
    ["blue", "plum", "pink", "rose", "lime", "stone", "slate"],
];
export const channelDataSetAccentSchema = z.enum([
    ...channelDataSetAccentRows[0],
    ...channelDataSetAccentRows[1],
]);
export const channelDataSetAccents = channelDataSetAccentSchema.options;
export const channelDataSetDerivedAccents = channelDataSetAccentRows[0];
// Object type metadata is shared between agents and people. Accent is the only
// key the product owns today, so writers must merge rather than replace.
export const CHANNEL_DATA_SET_ACCENT_METADATA_KEY = "accent";
export const channelDataViewTypes = [
    "table",
    "matrix",
    "board",
    "dashboard",
    "calendar",
    "timeline",
];
export const channelDataViewTypeSchema = z.enum(channelDataViewTypes);
export const channelViewTypeSchema = z.enum([
    ...channelDataViewTypes,
    "detail",
    "form",
    "custom",
]);
export const dashboardFieldKeySchema = z.string().trim().min(1);
export const matrixColumnBucketSchema = z.enum([
    "value",
    "day",
    "week",
    "month",
    "quarter",
    "year",
]);
export const matrixAggregateSchema = z.enum(["sum", "average", "min", "max", "count"]);
export const matrixValueFormatSchema = z.enum(["auto", "number", "currency", "percent"]);
const matrixMeasureIdSchema = z.string().trim().max(120).regex(/^[A-Za-z_][A-Za-z0-9_]*$/u);
export const matrixCalculationSchema = z.object({
    operator: z.enum(["add", "subtract", "multiply", "divide"]),
    // The ids of two measures listed earlier, in order: [revenue, cost] subtracts cost from revenue.
    operands: z.tuple([matrixMeasureIdSchema, matrixMeasureIdSchema]),
});
const matrixMeasureSchema = z.object({
    // Names the measure's row. Needed when two measures read one field, and by a calculation.
    id: matrixMeasureIdSchema.optional(),
    // The field to aggregate; a count counts records and names no field.
    fieldKey: dashboardFieldKeySchema.optional(),
    label: z.string().trim().min(1).max(120).optional(),
    // A calculated measure reads two earlier measures in place of a field. It is worked out from
    // their aggregates in every cell and total, so a margin is profit over revenue at each level
    // and never an average of margins. Formatted as a percent, a ratio of 0.42 reads 42%.
    calculation: matrixCalculationSchema.optional(),
    // Worked out for calculations to read, and given no row of its own.
    hidden: z.boolean().optional(),
    aggregate: matrixAggregateSchema.default("sum"),
    format: matrixValueFormatSchema.default("auto"),
    emphasis: z.enum(["normal", "subtotal", "total"]).default("normal"),
    level: z.number().int().min(0).max(3).default(0),
});
/** The key of a measure's row: its id, else its field, else "count". */
export function matrixMeasureId(measure) {
    return measure.id ?? measure.fieldKey ?? "count";
}
const matrixRowSortSchema = z.object({
    // "value" orders rows by a measure aggregated across every column shown; "field" by the smallest
    // value of a numeric field in each group, as a statement's line order puts its lines in order.
    by: z.enum(["label", "value", "field"]),
    // The measure to order by when rows hold several; left out, the first one shown.
    measure: matrixMeasureIdSchema.optional(),
    // The numeric field "field" orders by.
    fieldKey: dashboardFieldKeySchema.optional(),
    // Left out, labels run A to Z and values largest first.
    direction: z.enum(["asc", "desc"]).optional(),
});
const matrixColumnSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    bucket: matrixColumnBucketSchema.default("value"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    limit: z.number().int().min(1).max(120).default(120),
    completePeriodsOnly: z.boolean().default(false),
});
const dashboardWidgetDateContextSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    label: z.string().trim().min(1).max(120).optional(),
    periodStart: z.string().trim().min(1).max(40).optional(),
    periodEnd: z.string().trim().min(1).max(40).optional(),
});
export const dashboardWidgetBaseSchema = z.object({
    id: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1_000).optional(),
    dateContext: dashboardWidgetDateContextSchema.optional(),
    position: dashboardWidgetPositionSchema,
});
const dashboardSnapshotDataContractSchema = z.object({
    kind: z.literal("snapshot"),
    capturedAt: z.number().int().nonnegative(),
    source: z.string().trim().min(1).max(120),
    sourceRef: z.string().trim().min(1).max(500).optional(),
});
const dashboardLiveSummaryDataContractSchema = z.object({
    kind: z.literal("live_summary"),
    sourceWidgetIds: z.array(z.string().trim().min(1).max(120)).min(1).max(8)
        .refine((ids) => new Set(ids).size === ids.length, "Live-summary source widgets must be unique."),
    maxItems: z.number().int().min(1).max(8).default(4),
});
const dashboardNarrativeDataContractSchema = z.discriminatedUnion("kind", [
    dashboardSnapshotDataContractSchema,
    dashboardLiveSummaryDataContractSchema,
]);
const dashboardSelectFilterSchema = z.object({
    id: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(200),
    fieldKey: dashboardFieldKeySchema,
    type: z.literal("select").default("select"),
    allLabel: z.string().trim().min(1).max(80).default("All"),
    // Values left out when the dashboard opens ("everything except AP&T"); new
    // values stay included because the default applies as a "notIn" filter.
    defaultExcluded: z.array(z.string().trim().min(1).max(200)).min(1).max(50)
        .refine((values) => new Set(values).size === values.length, "Default excluded values must be unique.")
        .optional(),
});
// Date filters narrow every widget to a range on one date field. Presets are
// relative (resolved against the viewer's day), so "Year to date" rolls forward.
const dashboardDateFilterSchema = z.object({
    id: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(200),
    fieldKey: dashboardFieldKeySchema,
    type: z.literal("date"),
    presets: z.array(dashboardDatePresetSchema).min(1).max(dashboardDatePresetIds.length)
        .refine((presets) => new Set(presets).size === presets.length, "Date filter presets must be unique.")
        .default(DEFAULT_DASHBOARD_DATE_PRESETS),
    defaultPreset: dashboardDatePresetSchema.optional(),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
    // The month a financial year starts (1-12): 4 counts "Year to date", the year and the quarters from April.
    yearStartsOnMonth: z.number().int().min(1).max(12).default(1),
}).superRefine((filter, ctx) => {
    if (filter.defaultPreset && !filter.presets.includes(filter.defaultPreset)) {
        ctx.addIssue({
            code: "custom",
            path: ["defaultPreset"],
            message: `Default preset "${filter.defaultPreset}" must be one of the filter's presets.`,
        });
    }
});
// Filters saved before date filters existed omit type; they are select filters.
export const dashboardFilterSchema = z.preprocess((value) => value && typeof value === "object" && !Array.isArray(value) && !("type" in value)
    ? { ...value, type: "select" }
    : value, z.discriminatedUnion("type", [dashboardSelectFilterSchema, dashboardDateFilterSchema]));
export const dashboardMetricAggregateSchema = z.enum([
    "count",
    "sum",
    "average",
    "min",
    "max",
]);
export const dashboardDateBucketSchema = z.enum(["day", "week", "month", "year"]);
const dashboardPointSortSchema = z.object({
    direction: z.enum(["asc", "desc"]).default("desc"),
    mode: z.enum(["value", "absolute", "label"]).default("value"),
});
export const recordSortSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    direction: z.enum(["asc", "desc"]).default("desc"),
    mode: z.enum(["auto", "number", "date", "text"]).default("auto"),
});
/**
 * Filter value for user and multiuser fields that the control plane resolves to
 * the verified user making the request, so one saved view serves every viewer.
 */
export const CHANNEL_RECORD_FILTER_ME = "@me";
const channelRecordFilterValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
]);
const channelRecordSingleValueFilterSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    op: z.enum([
        "eq",
        "neq",
        "gt",
        "gte",
        "lt",
        "lte",
        "contains",
        "notContains",
        "startsWith",
        "endsWith",
    ]),
    value: channelRecordFilterValueSchema,
}).strict();
const channelRecordSetFilterSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    op: z.enum(["in", "notIn"]),
    values: z.array(channelRecordFilterValueSchema).min(1).max(50),
}).strict();
const channelRecordRangeFilterSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    op: z.enum(["between", "notBetween"]),
    values: z.array(channelRecordFilterValueSchema).length(2),
}).strict();
const channelRecordBlankFilterSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    op: z.enum(["isBlank", "isNotBlank"]),
}).strict();
export const channelRecordFilterSchema = z.union([
    channelRecordSingleValueFilterSchema,
    channelRecordSetFilterSchema,
    channelRecordRangeFilterSchema,
    channelRecordBlankFilterSchema,
]);
export const channelRecordFilterOperatorSchema = z.enum([
    "eq", "neq", "gt", "gte", "lt", "lte", "contains", "notContains",
    "startsWith", "endsWith", "in", "notIn", "between", "notBetween",
    "isBlank", "isNotBlank",
]);
const RECORD_FILTER_COMPARISON_OPERATORS = [
    "eq", "neq", "gt", "gte", "lt", "lte", "between", "notBetween", "isBlank", "isNotBlank",
];
const RECORD_FILTER_TEXT_OPERATORS = [
    "contains", "notContains", "startsWith", "endsWith", "eq", "neq", "isBlank", "isNotBlank",
];
const RECORD_FILTER_CHOICE_OPERATORS = [
    "in", "notIn", "eq", "neq", "isBlank", "isNotBlank",
];
export function channelRecordFilterOperators(fieldType) {
    if (["number", "currency", "percent", "date", "datetime"].includes(fieldType)) {
        return [...RECORD_FILTER_COMPARISON_OPERATORS];
    }
    if (fieldType === "multiselect" || fieldType === "multiuser") {
        return ["contains", "notContains", "in", "notIn", "isBlank", "isNotBlank"];
    }
    if (["text", "email", "url"].includes(fieldType))
        return [...RECORD_FILTER_TEXT_OPERATORS];
    if (["select", "user", "reference", "boolean"].includes(fieldType)) {
        return [...RECORD_FILTER_CHOICE_OPERATORS];
    }
    return [];
}
/**
 * A widget worked out from its data set's records, with the records it keeps to where it reads only some
 * of them: its own saved filter (widget-record-filters.ts). The filter is the widget's, so it reads the
 * same on a dashboard, a channel's Overview and a canvas. A note is written, or summed up from other
 * widgets, and reads no records.
 */
export const dashboardRecordWidgetBaseSchema = dashboardWidgetBaseSchema.extend({
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
});
/** A dashed reference line on a chart, such as a budget or a target. */
export const dashboardTargetSchema = z.object({
    value: z.number().finite(),
    label: z.string().trim().min(1).max(80).optional(),
}).strict();
/**
 * One bar of a waterfall: the sum of a numeric field. A start and an end are
 * levels drawn from zero; a change floats from the running total before it.
 */
export const dashboardWaterfallStepSchema = z.object({
    fieldKey: dashboardFieldKeySchema,
    label: z.string().trim().min(1).max(120),
    kind: z.enum(["start", "change", "end"]),
}).strict();
export const dashboardWidgetSchema = z.discriminatedUnion("type", [
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("metric"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema,
            fieldKey: dashboardFieldKeySchema.optional(),
        }),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("time_series"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema,
            fieldKey: dashboardFieldKeySchema.optional(),
        }),
        dimension: z.object({
            fieldKey: dashboardFieldKeySchema,
            bucket: dashboardDateBucketSchema.default("month"),
            completePeriodsOnly: z.boolean().optional(),
        }),
        limit: z.number().int().min(1).max(120).default(12),
        target: dashboardTargetSchema.optional(),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("breakdown"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema.default("count"),
            fieldKey: dashboardFieldKeySchema.optional(),
        }).default({ aggregate: "count" }),
        dimension: z.object({
            fieldKey: dashboardFieldKeySchema,
        }),
        variant: z.enum(["bar", "pie"]).default("bar"),
        limit: z.number().int().min(1).max(50).default(12),
        sort: dashboardPointSortSchema.default({ direction: "desc", mode: "value" }),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("bar"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema,
            fieldKey: dashboardFieldKeySchema.optional(),
        }),
        dimension: z.object({
            fieldKey: dashboardFieldKeySchema,
        }),
        variant: z.enum(["bar", "pie", "donut"]).default("bar"),
        limit: z.number().int().min(1).max(50).default(12),
        sort: dashboardPointSortSchema.default({ direction: "desc", mode: "value" }),
        /** Drawn on the bar variant; a pie or donut has no value axis. */
        target: dashboardTargetSchema.optional(),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("grouped_bar"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema,
            fieldKey: dashboardFieldKeySchema.optional(),
        }),
        dimension: z.object({
            fieldKey: dashboardFieldKeySchema,
        }),
        segment: z.object({
            fieldKey: dashboardFieldKeySchema,
        }),
        variant: z.enum(["grouped", "stacked"]).default("grouped"),
        limit: z.number().int().min(1).max(50).default(12),
        sort: dashboardPointSortSchema.default({ direction: "desc", mode: "value" }),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("stacked_bar"),
        metric: z.object({
            aggregate: dashboardMetricAggregateSchema,
            fieldKey: dashboardFieldKeySchema.optional(),
        }),
        dimension: z.object({
            fieldKey: dashboardFieldKeySchema,
            bucket: dashboardDateBucketSchema.default("year"),
            completePeriodsOnly: z.boolean().optional(),
        }),
        segment: z.object({
            fieldKey: dashboardFieldKeySchema,
        }),
        /** The latest periods shown; older history is left out of the result. */
        limit: z.number().int().min(1).max(366).default(120),
        /** Compared with each period's stacked total. */
        target: dashboardTargetSchema.optional(),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("waterfall"),
        steps: z.array(dashboardWaterfallStepSchema).min(2).max(12),
    }),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("record_list"),
        columns: z.array(dashboardFieldKeySchema).max(12).default([]),
        limit: z.number().int().min(1).max(100).default(10),
        sort: recordSortSchema.optional(),
    }),
    dashboardWidgetBaseSchema.extend({
        type: z.literal("insight_list"),
        dataContract: dashboardNarrativeDataContractSchema,
        items: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
        presentation: z.enum(["list", "table"]).default("list"),
        columnLabel: z.string().trim().min(1).max(120).default("Item"),
    }),
    dashboardWidgetBaseSchema.extend({
        type: z.literal("risk_list"),
        dataContract: dashboardNarrativeDataContractSchema,
        items: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
        presentation: z.enum(["list", "table"]).default("list"),
        columnLabel: z.string().trim().min(1).max(120).default("Item"),
    }),
]).superRefine((widget, ctx) => {
    if (widget.type === "waterfall") {
        widget.steps.forEach((step, index) => {
            if (step.kind === "start" && index !== 0) {
                ctx.addIssue({ code: "custom", path: ["steps", index, "kind"],
                    message: "A waterfall's start is its first step." });
            }
            if (step.kind === "end" && index !== widget.steps.length - 1) {
                ctx.addIssue({ code: "custom", path: ["steps", index, "kind"],
                    message: "A waterfall's end is its last step." });
            }
        });
        if (!widget.steps.some((step) => step.kind === "change")) {
            ctx.addIssue({ code: "custom", path: ["steps"],
                message: "A waterfall needs at least one change between its start and end." });
        }
        return;
    }
    if (widget.type !== "insight_list" && widget.type !== "risk_list")
        return;
    if (widget.dataContract.kind === "snapshot" && widget.items.length === 0) {
        ctx.addIssue({
            code: "custom",
            path: ["items"],
            message: "Snapshot narrative widgets require at least one saved item.",
        });
    }
    if (widget.dataContract.kind === "live_summary" && widget.items.length > 0) {
        ctx.addIssue({
            code: "custom",
            path: ["items"],
            message: "Live-summary narrative widgets derive their items from source widgets.",
        });
    }
});
/**
 * A saved note: text kept in the widget, written at one moment from its data set's records. It is shown only to
 * a reader who may read that data set, as the figures written from the same records are (Archie, 11 October 2026).
 */
export function isSavedNote(widget) {
    return (widget.type === "insight_list" || widget.type === "risk_list")
        && widget.dataContract?.kind === "snapshot";
}
// Saved table definitions carry columns either as bare field keys or as
// objects like { fieldKey } written by richer editors; reads accept both.
const tableViewColumnKeySchema = z.union([
    dashboardFieldKeySchema,
    z.object({ fieldKey: dashboardFieldKeySchema }).passthrough()
        .transform((column) => column.fieldKey),
]);
export const tableViewDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    columns: z.array(tableViewColumnKeySchema).default([]),
    filters: z.array(channelRecordFilterSchema).max(12).default([]),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
    /** Each group is ANDed internally; groups are ORed and then ANDed with recordFilters. */
    recordFilterGroups: z.array(z.array(channelRecordFilterSchema).min(1).max(4)).min(2).max(4).optional(),
    defaultSort: recordSortSchema.optional(),
}).passthrough();
/** Omitted settings are preserved; null clears sorting and [] clears filters/columns. */
export const tableViewPresentationPatchSchema = z.object({
    columns: tableViewDefinitionSchema.shape.columns.unwrap().optional(),
    recordFilters: tableViewDefinitionSchema.shape.recordFilters,
    defaultSort: recordSortSchema.nullable().optional(),
}).strict().refine((patch) => Object.values(patch).some((value) => value !== undefined), "At least one table presentation setting is required.");
const matrixViewBaseSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    column: matrixColumnSchema,
    // The first column's heading. Left out, rows of measures are headed "Account" and rows per
    // field value by the row fields' names.
    rowHeading: z.string().trim().min(1).max(120).optional(),
    // Row groups run in label order unless sorted otherwise. A row limit keeps the first groups
    // in that order (the largest ten, say); totals then cover the groups kept.
    rowSort: matrixRowSortSchema.optional(),
    rowLimit: z.number().int().min(1).max(500).optional(),
    // With two or three row fields: a row for each outer field's value, over all its rows, with
    // the inner values listed beneath it. A row limit then counts the outermost groups.
    subtotals: z.boolean().optional(),
    showRowTotals: z.boolean().default(false),
    showColumnTotals: z.boolean().default(false),
    // With date columns: a change column after them, the latest period against the one before it,
    // as an amount and as a percentage of the earlier period.
    showChange: z.boolean().optional(),
    emptyCell: z.enum(["dash", "blank", "zero"]).default("dash"),
    negativeStyle: z.enum(["minus", "parentheses"]).default("parentheses"),
    currencyCode: z.string().trim().regex(/^[A-Z]{3}$/u).optional(),
    decimals: z.number().int().min(0).max(4).optional(),
    // Shades value cells, not totals: "sequential" by size from the smallest to the largest,
    // "diverging" red below zero and green above, by distance from zero.
    colorScale: z.enum(["sequential", "diverging"]).optional(),
    filters: z.array(channelRecordFilterSchema).max(12).optional(),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
});
const matrixViewDefinitionBaseSchema = z.discriminatedUnion("rowMode", [
    matrixViewBaseSchema.extend({
        rowMode: z.literal("measures"),
        measures: z.array(matrixMeasureSchema).min(1).max(50),
        // Left out, each measure is one row. With row fields, every value of those fields is a
        // row group holding the measures.
        rowFields: z.array(dashboardFieldKeySchema).min(1).max(3).optional(),
    }).passthrough(),
    matrixViewBaseSchema.extend({
        rowMode: z.literal("field"),
        rowFields: z.array(dashboardFieldKeySchema).min(1).max(3),
        value: matrixMeasureSchema,
    }).passthrough(),
]).superRefine((definition, ctx) => {
    const measures = definition.rowMode === "measures" ? definition.measures : [definition.value];
    const measuresPath = definition.rowMode === "measures" ? "measures" : "value";
    const earlier = new Set();
    for (const measure of measures) {
        if (measure.calculation) {
            if (definition.rowMode === "field") {
                ctx.addIssue({ code: "custom", path: ["value", "calculation"],
                    message: "A calculation reads other measures; list them with it under rowMode 'measures', with rowFields for the row groups." });
            }
            if (measure.id === undefined || measure.fieldKey !== undefined) {
                ctx.addIssue({ code: "custom", path: [measuresPath, measure.id === undefined ? "id" : "fieldKey"],
                    message: "A calculated measure needs an id and takes no fieldKey." });
            }
            const unknown = measure.calculation.operands.filter((operand) => !earlier.has(operand));
            if (unknown.length > 0) {
                ctx.addIssue({ code: "custom", path: [measuresPath, "calculation", "operands"],
                    message: `A calculation reads measures listed before it; ${unknown.join(", ")} ${unknown.length === 1 ? "is" : "are"} not.` });
            }
        }
        else if (measure.aggregate === "count" && measure.fieldKey !== undefined) {
            ctx.addIssue({ code: "custom", path: [measuresPath, "fieldKey"],
                message: "A count counts records and takes no fieldKey." });
        }
        else if (measure.aggregate !== "count" && measure.fieldKey === undefined) {
            ctx.addIssue({ code: "custom", path: [measuresPath, "fieldKey"],
                message: `A ${measure.aggregate} measure needs the fieldKey of a number, currency, or percent field.` });
        }
        earlier.add(matrixMeasureId(measure));
    }
    if (measures.every((measure) => measure.hidden)) {
        ctx.addIssue({ code: "custom", path: [measuresPath],
            message: "A pivot table needs at least one measure that is not hidden." });
    }
    const rowFields = definition.rowFields ?? [];
    if (new Set(rowFields).size !== rowFields.length) {
        ctx.addIssue({ code: "custom", path: ["rowFields"], message: "Pivot table row fields must be unique." });
    }
    if (earlier.size !== measures.length) {
        ctx.addIssue({ code: "custom", path: [measuresPath],
            message: "Pivot table measures must be unique; give two measures of one field different ids." });
    }
    const measureFields = measures.flatMap((measure) => measure.fieldKey ?? []);
    if ([...rowFields, ...(definition.rowMode === "measures" ? measureFields : [])].includes(definition.column.fieldKey)) {
        ctx.addIssue({ code: "custom", path: ["column", "fieldKey"],
            message: "The pivot table column field cannot also be a row field or measure." });
    }
    if (definition.column.completePeriodsOnly && definition.column.bucket === "value") {
        ctx.addIssue({ code: "custom", path: ["column", "completePeriodsOnly"],
            message: "Complete-period filtering requires a date bucket." });
    }
    if (definition.showChange && definition.column.bucket === "value") {
        ctx.addIssue({ code: "custom", path: ["showChange"],
            message: "A change from the previous period needs columns by day, week, month, quarter or year." });
    }
    if (measureFields.some((fieldKey) => rowFields.includes(fieldKey))) {
        ctx.addIssue({ code: "custom", path: [measuresPath, "fieldKey"],
            message: "The pivot table value field cannot also be a row field." });
    }
    if (rowFields.length === 0 && (definition.rowSort || definition.rowLimit !== undefined)) {
        ctx.addIssue({ code: "custom", path: [definition.rowSort ? "rowSort" : "rowLimit"],
            message: "Rows of measures keep the order they are listed in; rowSort and rowLimit need rowFields." });
    }
    if (definition.subtotals && rowFields.length < 2) {
        ctx.addIssue({ code: "custom", path: ["subtotals"],
            message: "Subtotals need two or three rowFields: the outer fields' values are the subtotal rows." });
    }
    if ((definition.rowSort?.by === "field") !== (definition.rowSort?.fieldKey !== undefined)) {
        ctx.addIssue({ code: "custom", path: ["rowSort", "fieldKey"],
            message: "rowSort.fieldKey names the field to order by, with by 'field' and only then." });
    }
    if (definition.rowSort?.measure !== undefined && !earlier.has(definition.rowSort.measure)) {
        ctx.addIssue({ code: "custom", path: ["rowSort", "measure"],
            message: `rowSort.measure names no measure of this pivot table: ${definition.rowSort.measure}.` });
    }
    if ((definition.showRowTotals || definition.showColumnTotals)
        && measures.some((measure) => !measure.calculation && measure.aggregate !== "sum" && measure.aggregate !== "count")) {
        ctx.addIssue({ code: "custom", path: ["showRowTotals"],
            message: "Pivot table totals are available only for sum and count measures." });
    }
});
export const matrixViewDefinitionSchema = z.preprocess(normalizeSavedRecordFilters, matrixViewDefinitionBaseSchema);
export const matrixViewDataColumnSchema = z.object({
    key: z.string().trim().min(1),
    label: z.string().trim().min(1),
    sortValue: z.string(),
});
export const matrixViewDataRowSchema = z.object({
    /** Full grouping path, including ancestors hidden by indentation; empty for closing totals. */
    rowPath: z.array(z.string()).max(3).optional(),
    key: z.string().trim().min(1),
    // What the row's first cell shows, and for each label the field it is a value of; a label
    // that is a measure's name or "Total" has no field.
    labels: z.array(z.string()),
    labelFields: z.array(z.string().nullable()).optional(),
    // A heading names a row group whose measures follow as their own rows; it holds no numbers.
    heading: z.boolean().optional(),
    measureId: z.string().trim().min(1).optional(),
    fieldKey: z.string().trim().min(1).optional(),
    fieldType: z.string().trim().min(1),
    format: matrixValueFormatSchema,
    emphasis: z.enum(["normal", "subtotal", "total"]),
    level: z.number().int().min(0).max(3),
    values: z.record(z.string(), z.number().finite().nullable()),
    total: z.number().finite().nullable(),
    // With showChange: the latest period less the one before (an empty sum or count is 0), and that
    // as a fraction of the earlier period's size. Null where either can't be worked out.
    change: z.object({
        amount: z.number().finite().nullable(),
        percent: z.number().finite().nullable(),
    }).optional(),
});
export const matrixViewDataResponseSchema = z.object({
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    definition: matrixViewDefinitionSchema,
    generatedAt: z.number().int().nonnegative(),
    freshness: channelDataFreshnessSchema,
    dataSources: z.array(channelDataSourceSchema),
    columns: z.array(matrixViewDataColumnSchema).max(120),
    rows: z.array(matrixViewDataRowSchema).max(500),
    rowPage: z.object({
        limit: z.literal(500),
        hasMore: z.boolean(),
    }),
    // The closing Total rows, aggregated in the database over every row they cover: one row, or a
    // heading and a row per measure. columnTotals repeats the single row's cells by column.
    totalRows: z.array(matrixViewDataRowSchema).max(51).default([]),
    columnTotals: z.record(z.string(), z.number().finite().nullable()),
    // The two periods each row's change compares, once there are two to compare.
    change: z.object({
        columnKey: z.string().trim().min(1),
        previousColumnKey: z.string().trim().min(1),
        label: z.string().trim().min(1),
    }).optional(),
});
export const boardViewDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    groupBy: dashboardFieldKeySchema.optional(),
    groupByFieldKey: dashboardFieldKeySchema.optional(),
    columns: z.array(dashboardFieldKeySchema).default([]),
    cardFields: z.array(dashboardFieldKeySchema).max(12).default([]),
    filters: z.array(channelRecordFilterSchema).max(12).default([]),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
}).passthrough().superRefine((definition, ctx) => {
    if (definition.groupBy || definition.groupByFieldKey)
        return;
    ctx.addIssue({
        code: "custom",
        message: "Board view definitions require groupBy or groupByFieldKey.",
        path: ["groupBy"],
    });
}).transform((definition) => {
    const groupBy = definition.groupBy ?? definition.groupByFieldKey;
    return {
        ...definition,
        groupBy,
        groupByFieldKey: groupBy,
    };
});
export const calendarViewModeSchema = z.enum(["month", "week", "day", "agenda"]);
export const calendarViewDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    startField: dashboardFieldKeySchema,
    endField: dashboardFieldKeySchema.optional(),
    titleField: dashboardFieldKeySchema.optional(),
    colorField: dashboardFieldKeySchema.optional(),
    resourceField: dashboardFieldKeySchema.optional(),
    allDayField: dashboardFieldKeySchema.optional(),
    /** A number field holding the record's length, in minutes or (when the
     *  field is named in hours) hours, for datasets that have no end field. */
    durationField: dashboardFieldKeySchema.optional(),
    defaultMode: calendarViewModeSchema.default("week"),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
    timezone: z.string().trim().min(1).refine((value) => {
        try {
            new Intl.DateTimeFormat("en", { timeZone: value });
            return true;
        }
        catch {
            return false;
        }
    }, "Use a valid IANA timezone, such as Europe/London.").optional(),
    /** Explicit date-only companion to startField, synchronized by calendar edits. */
    startDateField: dashboardFieldKeySchema.optional(),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
}).passthrough();
export const timelineViewScaleSchema = z.enum(["week", "month", "quarter", "year"]);
export const timelineViewDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    startField: dashboardFieldKeySchema,
    endField: dashboardFieldKeySchema.optional(),
    titleField: dashboardFieldKeySchema.optional(),
    colorField: dashboardFieldKeySchema.optional(),
    groupField: dashboardFieldKeySchema.optional(),
    progressField: dashboardFieldKeySchema.optional(),
    defaultScale: timelineViewScaleSchema.default("quarter"),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
    timezone: calendarViewDefinitionSchema.shape.timezone,
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
}).passthrough();
const dashboardViewDefinitionBaseSchema = z.object({
    version: z.literal(1).default(1),
    objectTypeId: channelObjectTypeIdSchema.optional(),
    layout: dashboardLayoutSchema.default(() => ({ ...DASHBOARD_DEFAULT_LAYOUT })),
    filters: z.array(dashboardFilterSchema).max(12).default([]),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
    widgets: z.array(dashboardWidgetSchema).min(1).max(24),
}).superRefine((definition, ctx) => {
    for (const issue of dashboardLayoutIssues(definition.layout, definition.widgets)) {
        ctx.addIssue({ code: "custom", path: issue.path, message: issue.message });
    }
    const filterIds = new Map();
    for (const [index, filter] of definition.filters.entries()) {
        const previousIndex = filterIds.get(filter.id);
        if (previousIndex !== undefined) {
            ctx.addIssue({
                code: "custom",
                path: ["filters", index, "id"],
                message: `Filter id "${filter.id}" duplicates filters.${previousIndex}.id.`,
            });
        }
        else {
            filterIds.set(filter.id, index);
        }
    }
    const widgetsById = new Map(definition.widgets.map((widget) => [widget.id, widget]));
    for (const [index, widget] of definition.widgets.entries()) {
        if ((widget.type !== "insight_list" && widget.type !== "risk_list")
            || widget.dataContract.kind !== "live_summary")
            continue;
        for (const [sourceIndex, sourceWidgetId] of widget.dataContract.sourceWidgetIds.entries()) {
            const source = widgetsById.get(sourceWidgetId);
            if (!source) {
                ctx.addIssue({
                    code: "custom",
                    path: ["widgets", index, "dataContract", "sourceWidgetIds", sourceIndex],
                    message: `Live-summary source widget "${sourceWidgetId}" does not exist.`,
                });
            }
            else if (["record_list", "insight_list", "risk_list"].includes(source.type)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["widgets", index, "dataContract", "sourceWidgetIds", sourceIndex],
                    message: `Live summaries require metric or chart sources, not ${source.type}.`,
                });
            }
        }
    }
});
/**
 * A dashboard's definition as it is saved and read. Once checked as written, a widget saved fewer rows tall than its
 * type reads in on the grid's rows is made tall enough (withLeastRows), so no reader draws it cut short.
 */
export const dashboardViewDefinitionSchema = z.preprocess(normalizeSavedRecordFilters, dashboardViewDefinitionBaseSchema).transform((definition) => ({ ...definition, widgets: withLeastRows(definition.widgets, definition.layout) }));
function normalizeSavedRecordFilters(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return value;
    const definition = value;
    if (!Array.isArray(definition.filters) || definition.filters.length === 0) {
        return value;
    }
    const alias = channelRecordFilterSchema.array().max(12).safeParse(definition.filters);
    if (!alias.success)
        return value;
    const existing = channelRecordFilterSchema.array().max(12).safeParse(definition.recordFilters ?? []);
    if (!existing.success)
        return value;
    const { filters: _filters, ...rest } = definition;
    return {
        ...rest,
        recordFilters: [...existing.data, ...alias.data],
    };
}
export const channelViewVersionStatusSchema = z.enum([
    "draft",
    "published",
    "archived",
]);
export const channelGroupIdSchema = z.string().trim().min(1).max(200);
const channelGroupNameSchema = z.string().trim().min(1).max(80);
/**
 * A named section of an organisation's channel navigation. Every member sees
 * the same groups; a group grants no access to the channels filed in it.
 */
export const channelGroupSchema = z.object({
    id: channelGroupIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    name: channelGroupNameSchema,
    position: z.number().int().nonnegative(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const createChannelGroupInputSchema = z.object({
    organisationId: z.string().trim().min(1).optional(),
    name: channelGroupNameSchema,
}).strict();
export const patchChannelGroupInputSchema = z.object({
    name: channelGroupNameSchema.optional(),
    /** The group's zero-based place among its organisation's groups. */
    position: z.number().int().nonnegative().max(10_000).optional(),
}).strict().refine((input) => input.name !== undefined || input.position !== undefined, {
    message: "Name a new name or position for the group.",
});
/** Files a channel in a group, or takes it out of its group with null. */
export const setChannelGroupInputSchema = z.object({
    groupId: channelGroupIdSchema.nullable(),
}).strict();
/** A group with the active channels in it that the reader may open. */
export const channelGroupListingSchema = channelGroupSchema.extend({
    channelIds: z.array(channelIdSchema),
});
export const listChannelGroupsQuerySchema = z.object({
    organisationId: z.string().trim().min(1).optional(),
});
export const listChannelGroupsResponseSchema = z.object({
    groups: z.array(channelGroupListingSchema),
});
export const channelGroupResponseSchema = z.object({ group: channelGroupSchema });
export const channelSchema = z.object({
    id: channelIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    slug: channelSlugSchema,
    name: z.string().trim().min(1),
    description: z.string(),
    purpose: z.string(),
    visibility: channelVisibilitySchema,
    status: channelStatusSchema,
    /** Absent from servers that predate external channels, which are all internal. */
    audience: channelAudienceSchema.optional(),
    /** The navigation group the channel is filed in, if any. */
    groupId: channelGroupIdSchema.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
    // List-only aggregates (optional; present on GET /api/channels so the
    // channels view can show substance without N+1 record fetches).
    recordCount: z.number().int().min(0).optional(),
    objectTypeNames: z.array(z.string()).optional(),
});
export const channelObjectTypeSchema = z.object({
    id: channelObjectTypeIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    slug: channelSlugSchema,
    name: z.string().trim().min(1),
    description: z.string(),
    status: channelStatusSchema,
    metadata: z.record(z.string(), z.unknown()),
    datasetKind: z.enum(["source_view", "twin_product", "composed"]).optional(),
    fileUploadIntakeActive: z.boolean().optional(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const channelObjectFieldSchema = z.object({
    id: channelObjectFieldIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    key: z.string().trim().min(1),
    label: z.string().trim().min(1),
    type: channelObjectFieldTypeSchema.describe(CHANNEL_OBJECT_FIELD_TYPE_SELECTION_GUIDANCE),
    required: z.boolean(),
    position: z.number().int().nonnegative(),
    config: z.record(z.string(), z.unknown()),
    status: channelStatusSchema,
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const channelRecordSchema = z.object({
    id: channelRecordIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    title: z.string(),
    values: z.record(z.string(), z.unknown()),
    status: channelStatusSchema,
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    sourceSystem: z.string().trim().min(1).optional(),
    externalId: z.string().trim().min(1).optional(),
    sourceRef: z.record(z.string(), z.unknown()).optional(),
    /** Monotonic write counter; send back via If-Match for conflict-checked patches. */
    version: z.number().int().positive(),
    createdBy: z.string().trim().min(1),
    updatedBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const channelRecordListItemSchema = channelRecordSchema.extend({
    discussionCommentCount: z.number().int().nonnegative(),
});
export const channelViewSchema = z.object({
    id: channelViewIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema.optional(),
    slug: channelSlugSchema,
    name: z.string().trim().min(1),
    type: channelViewTypeSchema,
    status: channelViewStatusSchema,
    currentVersionId: z.string().trim().min(1).optional(),
    currentDefinition: z.record(z.string(), z.unknown()).optional(),
    config: z.record(z.string(), z.unknown()),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const channelViewVersionSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    version: z.number().int().positive(),
    status: channelViewVersionStatusSchema,
    definition: z.record(z.string(), z.unknown()),
    publishedAt: z.number().optional(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
});
export const channelFileSchema = z.object({
    id: channelFileIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    sessionId: z.string().trim().min(1).optional(),
    recordId: channelRecordIdSchema.optional(),
    fileName: z.string().trim().min(1),
    contentType: z.string().trim().min(1),
    byteSize: z.number().int().nonnegative(),
    storageProvider: z.string().trim().min(1),
    storageKey: z.string().trim().min(1),
    checksumSha256: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
});
/*
 * Organisation-level file (migration 0565). Channels, sessions and records
 * link to a file; `channelId`, `sessionId` and `recordId` echo the first link
 * of each kind so channel-shaped clients keep working.
 */
export const workspaceFileProvenanceSchema = z.enum(["upload", "link"]);
/** Who a file's own visibility lets read it, as people may set it. */
export const workspaceFileVisibilitySchema = z.enum(["linked", "organisation"]);
/**
 * A file's visibility as stored: also `share`, a Twin share's upload, which only the share serves and no generic file
 * route returns (docs/specs/twin-shares.md, section 1); nobody sets it except by uploading for a share.
 */
export const workspaceFileStoredVisibilitySchema = z.enum([...workspaceFileVisibilitySchema.options, "share"]);
export const workspaceFileLinkTargetSchema = z.enum(["channel", "session", "record"]);
export const workspaceFileLinkSchema = z.object({
    targetType: workspaceFileLinkTargetSchema,
    targetId: z.string().trim().min(1),
    createdAt: z.number(),
});
export const workspaceFileVerificationStatusSchema = z.enum(["unverified", "verified", "rejected"]);
export const workspaceFileSchema = z.object({
    id: channelFileIdSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    provenance: workspaceFileProvenanceSchema,
    fileName: z.string().trim().min(1),
    contentType: z.string().trim().min(1),
    byteSize: z.number().int().nonnegative(),
    storageProvider: z.string().trim().min(1),
    storageKey: z.string().trim().min(1),
    checksumSha256: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()),
    visibility: workspaceFileStoredVisibilitySchema,
    verification: z.object({
        status: workspaceFileVerificationStatusSchema,
        verifiedBy: z.string().trim().min(1).optional(),
        verifiedAt: z.number().optional(),
    }),
    links: z.array(workspaceFileLinkSchema),
    channelId: channelIdSchema.optional(),
    sessionId: z.string().trim().min(1).optional(),
    recordId: channelRecordIdSchema.optional(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
    /** Who can open it, on a channel's file list for its members. */
    access: resourceAccessSchema.optional(),
});
export const listWorkspaceFilesResponseSchema = z.object({
    files: z.array(workspaceFileSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const patchWorkspaceFileInputSchema = z
    .object({
    verification: workspaceFileVerificationStatusSchema.optional(),
    visibility: workspaceFileVisibilitySchema.optional(),
})
    .strict()
    .refine((value) => value.verification !== undefined || value.visibility !== undefined, {
    message: "Nothing to change.",
});
/** Public per-file cap used by web, native, and control-plane upload surfaces. */
export const WORKSPACE_FILE_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
/**
 * Storage provider recorded on file rows whose content lives in Google
 * Workspace. The storage key is the canonical Google URL; downloads redirect
 * to it instead of reading object storage.
 */
export const WORKSPACE_FILE_LINK_STORAGE_PROVIDER = "google-drive";
/** JSON body for registering a pasted Google Workspace link as a file. */
export const workspaceFileLinkInputSchema = z
    .object({
    url: z.string().trim().min(1).max(2048),
    title: z.string().trim().min(1).max(200).optional(),
    channelId: z.string().trim().min(1).optional(),
    sessionId: z.string().trim().min(1).optional(),
    visibility: workspaceFileVisibilitySchema.optional(),
})
    .strict();
export const WORKSPACE_FILE_MULTIPART_OVERHEAD_BYTES = 1024 * 1024;
export function workspaceFileMaxUploadBytes(configuredValue) {
    const configuredBytes = Number(configuredValue ?? "");
    if (!Number.isFinite(configuredBytes) || configuredBytes <= 0) {
        return WORKSPACE_FILE_MAX_UPLOAD_BYTES;
    }
    return Math.floor(configuredBytes);
}
export function workspaceFileUploadRequestExceedsLimit(headers, uploadLimit) {
    const contentLength = Number(headers.get("content-length") ?? "");
    return Number.isFinite(contentLength)
        && contentLength > uploadLimit + WORKSPACE_FILE_MULTIPART_OVERHEAD_BYTES;
}
export async function parseWorkspaceFileUploadRequest(request, uploadLimit) {
    if (workspaceFileUploadRequestExceedsLimit(request.headers, uploadLimit)) {
        return {
            ok: false,
            status: 413,
            title: "Upload too large",
            detail: workspaceFileUploadTooLargeDetail(uploadLimit),
        };
    }
    const formData = await request.formData().catch((error) => {
        console.error("[workspace-file-upload] failed to parse multipart upload", error);
        return null;
    });
    if (!formData) {
        return {
            ok: false,
            status: 400,
            title: "Invalid upload",
            detail: `The upload could not be read. Select a valid file up to ${formatWorkspaceFileBytes(uploadLimit)}.`,
        };
    }
    const file = formData.get("file");
    if (!(file instanceof File)) {
        return { ok: false, status: 400, title: "Invalid upload", detail: "file is required." };
    }
    if (file.size > uploadLimit) {
        return {
            ok: false,
            status: 413,
            title: "Upload too large",
            detail: workspaceFileUploadTooLargeDetail(uploadLimit, file.size),
        };
    }
    return {
        ok: true,
        channelId: optionalWorkspaceFileUploadValue(formData.get("channelId")),
        sessionId: optionalWorkspaceFileUploadValue(formData.get("sessionId")),
        recordId: optionalWorkspaceFileUploadValue(formData.get("recordId")),
        visibility: optionalWorkspaceFileUploadValue(formData.get("visibility")),
        purpose: optionalWorkspaceFileUploadValue(formData.get("purpose")),
        file,
    };
}
export function workspaceFileUploadTooLargeDetail(uploadLimit, fileSize) {
    const actualSize = fileSize === undefined ? "" : ` is ${formatWorkspaceFileBytes(fileSize)} and`;
    return `The selected file${actualSize} exceeds the ${formatWorkspaceFileBytes(uploadLimit)} per-file upload limit.`;
}
export function formatWorkspaceFileBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} ${bytes === 1 ? "byte" : "bytes"}`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const roundedUp = Math.ceil(value * 10) / 10;
    return `${roundedUp} ${units[unitIndex]}`;
}
function optionalWorkspaceFileUploadValue(value) {
    return typeof value === "string" && value.trim() ? value.trim() : null;
}
export const channelMemoryEntrySchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    memoryKey: z.string().trim().min(1),
    value: z.record(z.string(), z.unknown()),
    source: z.string().trim().min(1),
    status: z.enum(["active", "pending", "archived"]),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
    version: z.number().int().positive().optional(),
    confidence: z.number().min(0).max(1).optional(),
    lastEventSeq: z.number().int().positive().optional(),
});
export const channelMemberSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: channelIdSchema,
    userId: z.string().trim().min(1),
    principalType: channelPrincipalTypeSchema,
    displayLabel: z.string().trim().min(1).optional(),
    role: channelMemberRoleSchema,
    status: channelMemberStatusSchema,
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const listChannelsQuerySchema = z.object({
    organisationId: z.string().trim().min(1).optional(),
    status: channelStatusSchema.optional(),
    visibility: channelVisibilitySchema.optional(),
    /** Only the channels filed in this navigation group. */
    groupId: channelGroupIdSchema.optional(),
    query: z.string().trim().max(200).optional(),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(250).default(100),
    includeCounts: z
        .enum(["true", "false", "1", "0"])
        .optional()
        .transform((value) => value === undefined || value === "true" || value === "1"),
});
export const createChannelInputSchema = z.object({
    organisationId: z.string().trim().min(1).optional(),
    slug: channelSlugSchema.optional(),
    name: z.string().trim().min(1).max(200),
    description: z.string().max(5_000).default(""),
    purpose: z.string().max(5_000).default(""),
    visibility: channelVisibilitySchema.default("shared"),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const patchChannelInputSchema = z.object({
    channelId: channelIdSchema,
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5_000).optional(),
    purpose: z.string().max(5_000).optional(),
    visibility: channelVisibilitySchema.optional(),
    status: channelStatusSchema.optional(),
    /** Only a channel owner or organisation admin, never the Twin, changes who the channel is for. */
    audience: channelAudienceSchema.optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const listChannelMembersQuerySchema = z.object({
    status: channelMemberStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(250).default(100),
});
export const upsertChannelMemberInputSchema = z.object({
    channelId: channelIdSchema,
    userId: z.string().trim().min(1).max(256),
    principalType: channelPrincipalTypeSchema.default("user"),
    role: channelMemberRoleSchema.optional(),
});
export const patchChannelMemberInputSchema = z.object({
    channelId: channelIdSchema,
    userId: z.string().trim().min(1).max(256),
    principalType: channelPrincipalTypeSchema.default("user"),
    role: channelMemberRoleSchema.optional(),
    status: channelMemberStatusSchema.optional(),
}).refine((input) => input.role !== undefined || input.status !== undefined, {
    message: "At least one of role or status is required",
    path: ["role"],
});
export const createChannelObjectTypeInputSchema = z.object({
    channelId: channelIdSchema,
    slug: channelSlugSchema.optional(),
    name: z.string().trim().min(1).max(200),
    description: z.string().max(5_000).default(""),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const patchChannelObjectTypeInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5_000).optional(),
    status: channelStatusSchema.optional(),
    accent: channelDataSetAccentSchema.optional(),
}).refine((input) => input.name !== undefined || input.description !== undefined
    || input.status !== undefined || input.accent !== undefined, {
    message: "At least one of name, description, status, or accent is required",
});
export const createChannelObjectFieldInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    key: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
    label: z.string().trim().min(1).max(200),
    type: channelObjectFieldTypeSchema.describe(CHANNEL_OBJECT_FIELD_TYPE_SELECTION_GUIDANCE),
    required: z.boolean().default(false),
    position: z.number().int().nonnegative().default(0),
    config: z.record(z.string(), z.unknown()).default({}),
});
export const patchChannelObjectFieldInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    key: z.string().trim().min(1).max(120).regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
    label: z.string().trim().min(1).max(200).optional(),
    required: z.boolean().optional(),
    position: z.number().int().min(0).max(500).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    status: channelStatusSchema.optional(),
}).refine((input) => input.label !== undefined || input.required !== undefined
    || input.position !== undefined || input.config !== undefined || input.status !== undefined, {
    message: "At least one of label, required, position, config, or status is required",
});
/**
 * Where a record's data came from, when it mirrors a row in a connected
 * system: the system's name and that system's id for the row (a warehouse
 * row's source_external_id). Kept on the record so lineage is a fact rather
 * than something a matcher has to rediscover; creating again with the same
 * source updates the existing record.
 */
export const channelRecordSourceSchema = z.object({
    system: z.string().trim().min(1).max(200),
    externalId: z.string().trim().min(1).max(500),
}).strict();
export const createChannelRecordInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    title: z.string().max(500).default(""),
    values: z.record(z.string(), z.unknown()).default({}),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    source: channelRecordSourceSchema.optional(),
});
export const patchChannelRecordInputSchema = z.object({
    channelId: channelIdSchema,
    recordId: channelRecordIdSchema,
    title: z.string().max(500).optional(),
    values: z.record(z.string(), z.unknown()).optional(),
    status: channelStatusSchema.optional(),
    /** Merge only the supplied values, preserving other fields. */
    merge: z.boolean().optional(),
});
export const patchChannelRecordCommandInputSchema = patchChannelRecordInputSchema.extend({
    merge: z.boolean().default(false),
    expectedVersion: z.number().int().min(1).nullable().default(null),
});
/*
 * Several record or field writes in one command, so a run that fills a table or reshapes a data set makes one call,
 * not one per item. Every item is written or refused on its own; the answer gives each one's result by its index.
 */
export const CHANNEL_RECORD_BATCH_MAX = 100;
export const CHANNEL_FIELD_BATCH_MAX = 50;
export const createChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    records: z.array(z.object({
        title: z.string().max(500).default(""),
        values: z.record(z.string(), z.unknown()).default({}),
    }).strict()).min(1).max(CHANNEL_RECORD_BATCH_MAX),
}).strict();
export const patchChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    /** Merge each record's values into its stored ones, as a single update does by default for the Twin. */
    merge: z.boolean().default(true),
    records: z.array(z.object({
        recordId: channelRecordIdSchema,
        title: z.string().max(500).optional(),
        values: z.record(z.string(), z.unknown()).optional(),
        status: channelStatusSchema.optional(),
        expectedVersion: z.number().int().min(1).nullable().default(null),
    }).strict().refine((item) => item.title !== undefined || item.values !== undefined || item.status !== undefined, {
        message: "Each record needs at least one of title, values, or status",
    })).min(1).max(CHANNEL_RECORD_BATCH_MAX),
}).strict();
export const upsertChannelObjectFieldsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    fields: z.array(createChannelObjectFieldInputSchema.omit({ channelId: true, objectTypeId: true }).strict())
        .min(1).max(CHANNEL_FIELD_BATCH_MAX),
}).strict();
export const patchChannelObjectFieldsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    fields: z.array(z.object({
        key: z.string().trim().min(1).max(120).regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
        label: z.string().trim().min(1).max(200).optional(),
        required: z.boolean().optional(),
        position: z.number().int().min(0).max(500).optional(),
        config: z.record(z.string(), z.unknown()).optional(),
        status: channelStatusSchema.optional(),
    }).strict().refine((item) => item.label !== undefined || item.required !== undefined
        || item.position !== undefined || item.config !== undefined || item.status !== undefined, {
        message: "Each field needs at least one of label, required, position, config, or status",
    })).min(1).max(CHANNEL_FIELD_BATCH_MAX),
}).strict();
/** One item's outcome: what was written, or why it was refused and left unwritten. */
export const channelBatchWriteItemResultSchema = z.discriminatedUnion("ok", [
    z.object({ index: z.number().int().nonnegative(), ok: z.literal(true),
        record: channelRecordSchema.optional(), field: channelObjectFieldSchema.optional() }),
    z.object({ index: z.number().int().nonnegative(), ok: z.literal(false),
        error: z.object({ status: z.number().int(), title: z.string(), detail: z.string() }) }),
]);
export const channelBatchWriteResultSchema = z.object({
    results: z.array(channelBatchWriteItemResultSchema),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** The one data set the batch wrote to, named, so a run's record of what it changed can say which. */
    dataSet: z.object({ id: channelObjectTypeIdSchema, name: z.string() }).optional(),
});
/** A running turn's channel list: the channels its actor may open, newest first. */
export const listChannelsCommandInputSchema = z.object({
    status: z.enum(["active", "archived"]).default("active"),
    limit: z.number().int().min(1).max(100).default(50),
}).strict();
/** A running turn's view of one channel's Data sets, fields, bindings, views and optional samples. */
export const channelSchemaCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1).optional(),
    includeRecords: z.boolean().default(false),
    sampleLimit: z.number().int().min(1).max(100).default(20),
}).strict();
const recordReadStatusSchema = z.enum(["active", "archived"]).default("active");
const recordReadOffsetSchema = z.number().int().min(0).max(1_000_000).default(0);
/** A page of one channel's records with the true total; filters need the Data set. */
export const listChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1).optional(),
    status: recordReadStatusSchema,
    limit: z.number().int().min(1).max(200).default(100),
    offset: recordReadOffsetSchema,
    where: z.array(z.unknown()).max(12).default([]),
}).strict();
/** Filtered, sorted and selected records of one Data set; the schema validates each clause. */
export const queryChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1),
    status: recordReadStatusSchema,
    limit: z.number().int().min(1).max(100).default(25),
    offset: recordReadOffsetSchema,
    where: z.unknown().optional(),
    orderBy: z.unknown().optional(),
    select: z.unknown().optional(),
}).strict();
/** Counts and numeric aggregates over one Data set, optionally grouped by a field. */
export const aggregateChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1),
    status: recordReadStatusSchema,
    groupByFieldKey: z.string().trim().min(1).optional(),
    groupLimit: z.number().int().min(1).max(100).default(50),
    metrics: z.unknown().optional(),
}).strict();
/** The run's inbox items: its credential subject's, the organisation's and readable shared mailboxes'. */
export const searchInboxCommandInputSchema = z.object({
    source: z.string().trim().min(1).max(200).optional(),
    state: z.enum(["new", "dismissed", "promoted", "all"]).optional(),
    query: z.string().trim().min(1).max(2_000).optional(),
    limit: z.number().int().min(1).max(100).default(25),
}).strict();
/** One existing Data set or saved view the actor may open, resolved for the session panel. */
export const openDataSetViewCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1).optional(),
    viewId: z.string().trim().min(1).optional(),
}).strict().refine((input) => input.objectTypeId || input.viewId, {
    message: "Provide objectTypeId or viewId to open a Data set or saved View.",
});
/** How recently the organisation's sources ingested and its curated views refreshed. */
export const dataFreshnessCommandInputSchema = z.object({
    sourceSystem: z.string().trim().min(1).max(200).optional(),
    entity: z.string().trim().min(1).max(200).optional(),
}).strict();
/** How recently the connector data behind the named warehouse relations (`schema.relation`) was synced. */
export const relationFreshnessCommandInputSchema = z.object({
    relations: z.array(z.string().regex(/^[a-z_][a-z0-9_]{0,62}\.[a-z_][a-z0-9_]{0,126}$/u)).min(1).max(128),
}).strict();
/** One saved view with its current definition, for an edit that builds on it. */
export const getChannelViewCommandInputSchema = z.object({
    channelId: channelIdSchema,
    viewId: z.string().trim().min(1),
}).strict();
/** A complete CSV export of one Data set's records, stored as an artifact of the running turn. */
export const exportChannelRecordsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1),
    title: z.string().trim().min(1).max(500),
    filename: z.string().trim().min(1).max(200),
    status: recordReadStatusSchema,
    limit: z.number().int().min(1).max(100_000).default(100_000),
    allowTruncated: z.boolean().default(false),
    fieldKeys: z.array(z.string().trim().min(1)).max(50).optional(),
    where: z.unknown().optional(),
    toolCallId: z.string().trim().min(1).max(500),
}).strict();
export const listChannelFilesCommandInputSchema = z.object({
    channelId: channelIdSchema,
    limit: z.number().int().min(1).max(250).default(100),
});
export const deleteChannelFileCommandInputSchema = z.object({
    channelId: channelIdSchema,
    fileId: channelFileIdSchema,
});
export const channelFileSummarySchema = z.object({
    id: channelFileIdSchema,
    channelId: channelIdSchema,
    sessionId: z.string().trim().min(1).optional(),
    recordId: channelRecordIdSchema.optional(),
    fileName: z.string().trim().min(1),
    contentType: z.string().trim().min(1),
    byteSize: z.number().int().nonnegative(),
    createdAt: z.number(),
});
export const listChannelMembersCommandInputSchema = listChannelMembersQuerySchema.extend({
    channelId: channelIdSchema,
});
export const linkInboxItemsCommandInputSchema = z.object({
    channelId: channelIdSchema,
    inboxItemIds: z.array(z.string().trim().min(1)).min(1).max(100)
        .transform((ids) => [...new Set(ids)]),
    promote: z.boolean().default(true),
    linkReason: z.string().trim().max(256).default("channel"),
});
export const recordSourceActionCommandInputSchema = createSourceActionReceiptRequestSchema
    .omit({ sourceSessionId: true, sourceRunId: true })
    .extend({ inboxItemId: z.string().trim().min(1).max(256).optional() });
export const hubspotCrmObjectTypeSchema = z.enum(["contacts", "companies", "deals"]);
export const hubspotCrmPropertiesSchema = z.record(z.string().trim().min(1).max(128).regex(/^[A-Za-z0-9_]+$/u), z.string().max(16_384)).refine((properties) => Object.keys(properties).length > 0, {
    message: "At least one HubSpot property is required",
}).refine((properties) => Object.keys(properties).length <= 100, {
    message: "At most 100 HubSpot properties are allowed",
});
export const hubspotCrmCreateInputSchema = z.object({
    objectType: hubspotCrmObjectTypeSchema,
    properties: hubspotCrmPropertiesSchema,
}).strict();
export const hubspotCrmUpdateInputSchema = hubspotCrmCreateInputSchema.extend({
    objectId: z.string().trim().min(1).max(128).regex(/^[0-9]+$/u),
}).strict();
const workerChannelCommandEnvelopeSchema = z.object({
    writeIntentId: z.string().trim().min(1).optional(),
    warehouseGrant: accessExecutionGrantV1Schema.optional(),
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    runId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
});
/**
 * The epoch millisecond after which a worker command's caller will no longer read its answer. A command still
 * running then rolls back instead of committing, so it never does what its caller has reported as failed.
 */
export const WORKER_COMMAND_DEADLINE_HEADER = "x-mlx-deadline-at";
export const workerChannelCommandRequestSchema = z.discriminatedUnion("command", [
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("feed.list"),
        input: z.object({
            channelId: channelIdSchema,
            cursor: z.string().trim().min(1).max(2048).optional(),
            limit: z.number().int().min(1).max(100).default(20),
        }).strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("post.create"),
        input: createChannelPostCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("post.update"),
        input: updateChannelPostCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("post.delete"),
        input: deleteChannelPostCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("thread.get"),
        input: getChannelFeedThreadCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("comment.create"),
        input: createChannelFeedCommentCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("comment.update"),
        input: updateChannelFeedCommentCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("comment.delete"),
        input: deleteChannelFeedCommentCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("reaction.set"),
        input: setChannelFeedReactionCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("channel.create"),
        input: createChannelInputSchema.omit({ organisationId: true }).strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("channel.patch"),
        // The Twin never changes who a channel is for.
        input: patchChannelInputSchema.omit({ audience: true }).strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-type.upsert"),
        input: createChannelObjectTypeInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-type.patch"),
        input: patchChannelObjectTypeInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-field.upsert"),
        input: createChannelObjectFieldInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-field.patch"),
        input: patchChannelObjectFieldInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.create"),
        input: createChannelRecordInputSchema.omit({ sourceSessionId: true, sourceRunId: true }).strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.patch"),
        input: patchChannelRecordCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.create-batch"),
        input: createChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.patch-batch"),
        input: patchChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-field.upsert-batch"),
        input: upsertChannelObjectFieldsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("object-field.patch-batch"),
        input: patchChannelObjectFieldsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("view.create"),
        input: z.lazy(() => createChannelViewInputSchema.strict()),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("view.patch"),
        input: z.lazy(() => patchChannelViewInputSchema.strict()),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("channel.list"),
        input: listChannelsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("channel.schema"),
        input: channelSchemaCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.list"),
        input: listChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.query"),
        input: queryChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.aggregate"),
        input: aggregateChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("inbox.search"),
        input: searchInboxCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("resource.open"),
        input: openDataSetViewCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("freshness.read"),
        input: dataFreshnessCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("freshness.relations"),
        input: relationFreshnessCommandInputSchema,
    }).strict(),
    // What depends on a data set, field, view or channel (or, for organisation admins, a view contract or relation).
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("doctor.impact"),
        input: twinDoctorImpactQuerySchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("view.get"),
        input: getChannelViewCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("record.export"),
        input: exportChannelRecordsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("file.list"),
        input: listChannelFilesCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("file.delete"),
        input: deleteChannelFileCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("member.list"),
        input: listChannelMembersCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("member.upsert"),
        input: upsertChannelMemberInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("member.patch"),
        input: patchChannelMemberInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("source-bundle.upsert"),
        input: sourceBackedChannelInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("twin-product-bundle.upsert"),
        input: twinProductChannelInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("inbox.link"),
        input: linkInboxItemsCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("source-action.record"),
        input: recordSourceActionCommandInputSchema.strict(),
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("hubspot.record.create"),
        writeIntentId: z.string().trim().min(1),
        input: hubspotCrmCreateInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("hubspot.record.update"),
        writeIntentId: z.string().trim().min(1),
        input: hubspotCrmUpdateInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("email-attachment.find"),
        input: findEmailAttachmentsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("email-attachment.add"),
        input: addEmailAttachmentsCommandInputSchema,
    }).strict(),
    workerChannelCommandEnvelopeSchema.extend({
        command: z.literal("curated-view.propose"),
        input: proposeCuratedViewCommandInputSchema,
    }).strict(),
]);
// Channel reads answer with stored rows; the runtime shapes them for the agent.
const channelReadRowSchema = z.record(z.string(), z.unknown());
/** A source-backed or Twin-product Data Set as attached: its channel, Data Set, fields and views. */
export const channelSourceBundleSchema = z.object({
    channel: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
    objectType: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
    dataset: z.object({ id: z.string(), schemaName: z.string(), relationName: z.string(),
        sourceSystem: z.string(), externalIdColumn: z.string().optional(),
        externalIdColumns: z.array(z.string()).optional(),
        datasetKind: z.enum(["source_view", "twin_product"]).optional(),
        productSlug: z.string().optional(), productPublicationId: z.string().optional(),
        productPublicationVersionId: z.string().optional(), productVersionId: z.string().optional() }),
    fields: z.array(z.object({ key: z.string(), label: z.string(), type: z.string(),
        sourceColumn: z.string().optional(), valueOwner: z.enum(["source", "overlay"]) })),
    views: z.array(z.object({ id: z.string(), name: z.string(), type: channelDataViewTypeSchema,
        slug: z.string() })),
});
export const workerChannelCommandResponseSchema = z.union([
    z.object({ channelRows: z.array(channelReadRowSchema) }),
    z.object({ emailAttachments: emailAttachmentSearchResultSchema }),
    z.object({ emailAttachmentImport: emailAttachmentAddResultSchema }),
    z.object({ curatedViewProposal: curatedViewProposalResultSchema }),
    z.object({ channelSchemaRows: z.object({
            objectTypes: z.array(channelReadRowSchema),
            fields: z.array(channelReadRowSchema),
            datasets: z.array(channelReadRowSchema),
            views: z.array(channelReadRowSchema),
            records: z.array(channelReadRowSchema),
            recordCount: z.number().int().nonnegative().nullable(),
            queriedRelations: z.array(z.unknown()),
            /** Data sets left out of `records` and `recordCount` because they could not be read, each with why. */
            recordsUnavailable: z.array(z.object({ objectTypeId: z.string().min(1), reason: z.string().min(1) })).optional(),
        }) }),
    z.object({ recordPage: z.object({
            totalCount: z.number().int().nonnegative(),
            rows: z.array(channelReadRowSchema),
            queriedRelations: z.array(z.unknown()),
        }) }),
    z.object({ recordQuery: z.object({
            totalCount: z.number().int().nonnegative(),
            rows: z.array(channelReadRowSchema),
            queriedRelations: z.array(z.unknown()),
            filters: z.array(z.record(z.string(), z.unknown())),
            orderBy: z.array(z.record(z.string(), z.unknown())),
            select: z.array(z.string()),
        }) }),
    // hasMore: more items matched than the limit returned. A tenant older than the field leaves it out.
    z.object({ inboxRows: z.array(channelReadRowSchema), hasMore: z.boolean().optional() }),
    z.object({ viewRow: channelReadRowSchema }),
    z.object({ exportedArtifact: z.object({
            artifact: z.record(z.string(), z.unknown()),
            provenance: z.record(z.string(), z.unknown()),
        }) }),
    z.object({ openedResource: z.object({
            channelId: z.string(), dataSetId: z.string(), viewId: z.string().optional(),
            title: z.string(), viewType: z.string(),
        }) }),
    z.object({ freshnessRows: z.object({
            pipelines: z.array(channelReadRowSchema),
            projections: z.array(channelReadRowSchema),
        }) }),
    // Null freshness: no requested relation is fed by a connector the organisation has enabled.
    z.object({ relationFreshness: z.object({
            freshness: channelDataFreshnessSchema.nullable(),
            lineage: z.array(z.object({ relation: z.string(), sourceSystem: z.string(), entity: z.string() }).strict()),
        }).strict() }),
    z.object({ doctorImpact: twinDoctorImpactResponseSchema }),
    z.object({ recordAggregate: z.object({
            totalCount: z.number().int().nonnegative(),
            queriedRelations: z.array(z.unknown()),
            metrics: z.unknown(),
            currencyBasis: z.unknown(),
            groups: z.unknown(),
        }) }),
    z.object({ feed: listChannelFeedResponseSchema }),
    z.object({ post: channelPostSchema }),
    z.object({ deletedPostId: z.string().trim().min(1) }),
    z.object({ thread: channelFeedThreadSchema }),
    z.object({ comment: channelFeedCommentSchema }),
    z.object({ deletedCommentId: z.string().trim().min(1) }),
    z.object({ engagement: channelFeedEngagementSchema }),
    z.object({ channel: channelSchema, created: z.boolean().optional() }),
    z.object({ objectType: channelObjectTypeSchema }),
    z.object({ field: channelObjectFieldSchema }),
    z.object({ record: channelRecordSchema }),
    z.object({ batch: channelBatchWriteResultSchema }),
    z.object({ view: channelViewSchema, version: channelViewVersionSchema.optional() }),
    z.object({ files: z.array(channelFileSummarySchema) }),
    z.object({
        file: channelFileSummarySchema,
        objectCleanup: z.enum(["deleted", "not_required", "failed"]),
    }),
    z.object({ members: z.array(channelMemberSchema) }),
    z.object({ member: channelMemberSchema }),
    z.object({ inboxLink: z.object({
            channelId: z.string(), linkedCount: z.number().int().nonnegative(),
            requestedCount: z.number().int().positive(), inboxItems: z.array(z.record(z.string(), z.unknown())),
        }) }),
    z.object({ sourceActionReceipt: z.record(z.string(), z.unknown()) }),
    z.object({ hubspotRecord: z.object({
            objectType: hubspotCrmObjectTypeSchema,
            objectId: z.string().trim().min(1),
            properties: z.record(z.string(), z.unknown()),
            createdAt: z.string().optional(),
            updatedAt: z.string().optional(),
            receiptId: z.string().trim().min(1),
        }) }),
    z.object({ sourceBundle: channelSourceBundleSchema }),
]);
export const upsertChannelRecordByExternalIdInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    sourceSystem: z.string().trim().min(1).max(100),
    externalId: z.string().trim().min(1).max(500),
    title: z.string().max(500).default(""),
    values: z.record(z.string(), z.unknown()).default({}),
    sourceRef: z.record(z.string(), z.unknown()).optional(),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
});
export const createChannelViewInputSchema = z.object({
    channelId: channelIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    slug: channelSlugSchema.optional(),
    name: z.string().trim().min(1).max(200),
    type: channelDataViewTypeSchema.default("table"),
    config: z.record(z.string(), z.unknown()).default({}),
    definition: z.record(z.string(), z.unknown()).default({}),
    makeDefault: z.boolean().default(false),
}).superRefine((input, ctx) => {
    const schema = input.type === "dashboard"
        ? dashboardViewDefinitionSchema
        : input.type === "table"
            ? tableViewDefinitionSchema
            : input.type === "matrix"
                ? matrixViewDefinitionSchema
                : input.type === "board"
                    ? boardViewDefinitionSchema
                    : input.type === "calendar"
                        ? calendarViewDefinitionSchema
                        : input.type === "timeline"
                            ? timelineViewDefinitionSchema
                            : null;
    if (!schema)
        return;
    const result = schema.safeParse(input.definition);
    if (!result.success) {
        for (const issue of result.error.issues) {
            ctx.addIssue({
                ...issue,
                path: ["definition", ...issue.path],
            });
        }
    }
});
export const patchChannelViewInputSchema = z.object({
    channelId: channelIdSchema,
    viewId: z.string().trim().min(1),
    expectedCurrentVersionId: z.string().trim().min(1).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    status: channelViewStatusSchema.optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    definition: z.record(z.string(), z.unknown()).optional(),
    tablePresentation: tableViewPresentationPatchSchema.optional(),
    widgetPositions: z.record(z.string().trim().min(1), dashboardWidgetPositionSchema)
        .refine((positions) => Object.keys(positions).length >= 1, "At least one widget position is required.")
        .refine((positions) => Object.keys(positions).length <= 24, "At most 24 widget positions are allowed.")
        .optional(),
    makeDefault: z.boolean().optional(),
}).superRefine((input, ctx) => {
    if (input.name === undefined
        && input.status === undefined
        && input.config === undefined
        && input.definition === undefined
        && input.tablePresentation === undefined
        && input.widgetPositions === undefined
        && input.makeDefault !== true) {
        ctx.addIssue({ code: "custom", message: "At least one view change is required." });
    }
    if (input.definition !== undefined && input.widgetPositions !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["widgetPositions"],
            message: "Send either a complete definition or widgetPositions, not both.",
        });
    }
    if (input.tablePresentation !== undefined
        && (input.definition !== undefined || input.widgetPositions !== undefined)) {
        ctx.addIssue({ code: "custom", path: ["tablePresentation"],
            message: "Send tablePresentation on its own, without definition or widgetPositions." });
    }
    if ((input.definition !== undefined || input.widgetPositions !== undefined || input.tablePresentation !== undefined)
        && input.expectedCurrentVersionId === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["expectedCurrentVersionId"],
            message: "Definition changes require expectedCurrentVersionId.",
        });
    }
});
export const listChannelsResponseSchema = z.object({
    channels: z.array(channelSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const channelResponseSchema = z.object({ channel: channelSchema });
export const listChannelMembersResponseSchema = z.object({
    members: z.array(channelMemberSchema),
});
export const channelMemberResponseSchema = z.object({ member: channelMemberSchema });
export const listChannelObjectTypesResponseSchema = z.object({
    objectTypes: z.array(channelObjectTypeSchema),
});
export const channelObjectTypeResponseSchema = z.object({
    objectType: channelObjectTypeSchema,
});
export const listChannelObjectFieldsResponseSchema = z.object({
    fields: z.array(channelObjectFieldSchema),
});
export const channelObjectFieldResponseSchema = z.object({
    field: channelObjectFieldSchema,
});
export const listChannelRecordsResponseSchema = z.object({
    records: z.array(channelRecordListItemSchema),
    hasMore: z.boolean().optional(),
    nextOffset: z.number().int().nonnegative().nullable().optional(),
    nextCursor: z.string().nullable().optional(),
    boardSummaryField: z.string().optional(),
    boardSummary: z.array(z.object({
        key: z.string(),
        count: z.number().int().nonnegative(),
        subtotal: z.number().nullable(),
    })).optional(),
});
export const channelRecordResponseSchema = z.object({
    record: channelRecordSchema,
});
export const listChannelViewsResponseSchema = z.object({
    views: z.array(channelViewSchema),
});
export const channelViewResponseSchema = z.object({
    view: channelViewSchema,
    version: channelViewVersionSchema.optional(),
});
export const listChannelFilesResponseSchema = z.object({
    files: z.array(channelFileSchema),
});
export const channelFileResponseSchema = z.object({
    file: channelFileSchema,
    objectCleanup: z.enum(["deleted", "not_required", "failed"]).optional(),
});
export const listChannelMemoryResponseSchema = z.object({
    memory: z.array(channelMemoryEntrySchema),
});
/*
 * A Data Set reading a view contract, in a channel its reader may open: where it is, what it is called, and the
 * version it reads, which is the one serving the contract.
 */
export const channelDataSetSummarySchema = z.object({
    channelId: channelIdSchema,
    channelName: z.string().trim().min(1),
    objectTypeId: channelObjectTypeIdSchema,
    name: z.string().trim().min(1),
    productVersionId: z.string().trim().min(1),
});
export const listChannelDataSetsQuerySchema = z.object({
    /** The view contract (Twin Build product) whose Data Sets to list. */
    viewContractProductId: z.string().trim().min(1).max(200),
});
export const listChannelDataSetsResponseSchema = z.object({
    dataSets: z.array(channelDataSetSummarySchema),
});
/** A Data Set another one can take values from, with the fields it can be matched on and give back. */
export const channelDataSetFieldsSchema = z.object({
    channelId: channelIdSchema,
    channelName: z.string().trim().min(1),
    objectTypeId: channelObjectTypeIdSchema,
    name: z.string().trim().min(1),
    fields: z.array(z.object({
        key: z.string().trim().min(1),
        label: z.string(),
        type: channelObjectFieldTypeSchema,
    })),
});
export const listChannelDataSetFieldsResponseSchema = z.object({
    dataSets: z.array(channelDataSetFieldsSchema),
});
